import React, {useEffect, useMemo, useRef} from 'react'
import clsx from 'clsx'
import styles from './UIGamePlayerInfoContainerAll.module.scss'
import {useGameStore} from '../../store/GameStoreContext.ts'
import {type PlayerInfoSection, UIGamePlayerInfoContainer} from '../UIGamePlayerInfoContainer/UIGamePlayerInfoContainer.tsx'
import {FadeScrollContainer} from '../../common/FadeScrollContainer/FadeScrollContainer.tsx'
import {useCurrentStateValidator} from '../../store/selectors/useCurrentStateValidator.ts'
import {type PlayerColor} from '@colonist/game-states'
import {useMyPerspectiveColor} from '../../store/selectors/useMyPerspectiveColor.ts'
import {type PlayerColorWithoutNone} from '../../utils/gameColors.ts'

export interface UIGamePlayerInfoContainerAllProps {
    visibleSections: PlayerInfoSection[]
}

export const UIGamePlayerInfoContainerAll = ({
    visibleSections,
}: UIGamePlayerInfoContainerAllProps) => {
    const playOrder = useGameStore((state) => state.gameDetails.playOrder)
    const myPerspectiveColor = useMyPerspectiveColor()
    const playOrderWhereMyPerspectiveIsFirst = useMemo(() => {
        const myPerspectiveIndex = playOrder.indexOf(myPerspectiveColor)
        if(myPerspectiveIndex === -1) return playOrder
        return playOrder.slice(myPerspectiveIndex).concat(playOrder.slice(0, myPerspectiveIndex))
    }, [playOrder, myPerspectiveColor])
    const opponentsPlayOrder = useMemo(() => playOrderWhereMyPerspectiveIsFirst.slice(1), [playOrderWhereMyPerspectiveIsFirst])

    const currentStateValidator = useCurrentStateValidator()
    const exclusiveCurrentTurnPlayer = currentStateValidator.currentTurnPlayers.length === 1
        ? currentStateValidator.currentTurnPlayers[0]
        : undefined

    const opponentRefs = useRef(new Map<PlayerColor, HTMLDivElement>())
    const playerCountClassName = playOrder.length === 2 ? styles.twoPlayers
        : playOrder.length === 3 ? styles.threePlayers
            : undefined

    useEffect(() => {
        if(exclusiveCurrentTurnPlayer === undefined || exclusiveCurrentTurnPlayer === myPerspectiveColor) return

        const opponentElement = opponentRefs.current.get(exclusiveCurrentTurnPlayer)
        opponentElement?.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'nearest'})
    }, [exclusiveCurrentTurnPlayer, myPerspectiveColor])

    return (
        <div className={clsx(styles.container, playerCountClassName)}>
            <div className={styles.playerColorWrapper} data-player-color={myPerspectiveColor}>
                <UIGamePlayerInfoContainer playerColor={myPerspectiveColor} visibleSections={visibleSections} />
            </div>
            <FadeScrollContainer fadeWidth={6} className={styles.opponentsContainer}>
                {opponentsPlayOrder.map((playerColor) => (
                    <div key={playerColor} className={styles.playerColorWrapper} data-player-color={playerColor} ref={(el) => {
                        if(el) opponentRefs.current.set(playerColor, el)
                        else opponentRefs.current.delete(playerColor)
                    }}>
                        <UIGamePlayerInfoContainer playerColor={playerColor as PlayerColorWithoutNone} visibleSections={visibleSections} />
                    </div>
                ))}
            </FadeScrollContainer>
        </div>
    )
}
